/**
 * @file Renders the page header with its title, menu and search input.
 */
import React from "react";
import type { ReactElement, ReactNode } from "react";
import { Space, Typography, Row, Col } from "antd";
import type { MenuProps } from "antd";

import { DropdownButton } from "@components/Button";
import { W0, W1, HEADER_HEIGHT } from "@components/Constants";

import SearchInput from "./SearchInput";

const { Text } = Typography;

type Props = {
  menu?: MenuProps["items"];
  onSearch?: (value: string) => void;
  titleExtra?: ReactNode;
  title: string;
};

/**
 * Renders the page's title (or a dropdown menu, when one is given) plus
 * an optional search input.
 */
const Header = ({
  menu = undefined,
  onSearch = undefined,
  titleExtra = undefined,
  title,
}: Props): JSX.Element => {
  const hasMenu =
    !!menu &&
    menu.length > 0;

  const paddingLeft = hasMenu
    ? W0
    : W1;

  const style = {
    height: HEADER_HEIGHT,
    paddingLeft: paddingLeft + 6,
    paddingRight: W1 + 6,
  };

  return (
    <Row align="middle" style={style}>
      <Col flex="auto">
        <Space>
          {
            hasMenu ? (
              <DropdownButton
                name={title}
                items={menu}
                dropdownRender={(items) => (
                  <div style={{ minWidth: 180 }}>
                    {React.cloneElement(items as ReactElement, {})}
                  </div>
                )}
              />
            ) : (
              <Text strong style={{ fontSize: 17, paddingLeft: 2, paddingRight: 4 }}>
                {title}
              </Text>
            )
          }
          {titleExtra && titleExtra}
        </Space>
      </Col>

      {
        onSearch && (
          <Col>
            <SearchInput onSearch={onSearch} />
          </Col>
        )
      }
    </Row>
  );
};

export default Header;
